<script setup lang="ts">
import { computed, watch } from 'vue';
import SplitView from '@/components/SplitView.vue';
import { useResize } from '@/composables/use-resize';
import { useWorkspaceContext } from '@/composables/use-workspace-context';
import {
	collectPanels,
	type PanelRegion,
	type PanelSystem,
	type RegionRoot,
} from '@/composables/use-panels';

const props = defineProps<{
	regionRoot: RegionRoot;
	panels: PanelSystem;
	region: PanelRegion;
	minSize?: number;
	maxSize?: number;
}>();

const isSide = computed(() => props.region === 'left' || props.region === 'right');

const { layout } = useWorkspaceContext();

// read initial state from persisted layout
const regionKey = props.region as 'left' | 'right' | 'bottom';
const defaultSize = isSide.value ? 280 : 250;
const dock = layout.value[regionKey];
const initialSize = dock?.size ?? defaultSize;

const resize = useResize({
	axis: isSide.value ? 'horizontal' : 'vertical',
	edge: props.region === 'left' ? 'end' : 'start',
	initial: initialSize,
	min: props.minSize ?? 0,
	max: props.maxSize ?? 600,
});

// persist size changes back to layout store
watch(resize.size, (newSize) => {
	layout.value = { ...layout.value, [regionKey]: { ...layout.value[regionKey], size: newSize } };
});

// all panels in this region's tree
const regionPanels = computed(() => collectPanels(props.regionRoot.root));

// minimized state — content area hidden, tab bar still visible
const minimized = computed(() => {
	if (dock.minimized) return true;
	if (regionPanels.value.length === 0) return true;
	return false;
});

// persist minimized state back to layout store
watch(minimized, (val) => {
	layout.value = { ...layout.value, [regionKey]: { ...layout.value[regionKey], minimized: val } };
});

// true when an active drag could target this dock — used to show empty docks as drop targets
const isDropTarget = computed(() => {
	const uri = props.panels.draggingUri.value;
	if (!uri) return false;
	return props.panels.canMoveToRegion(uri, props.region);
});

// handles dock minimization and maximization
const onTabMouseUp = (_uri: string, wasActive: boolean) => {
	// clicking the already-active tab while content is visible — minimize
	if (wasActive && !minimized.value) {
		dock.minimized = true;
	}
	// clicking any tab while minimized — restore
	else if (minimized.value) {
		dock.minimized = false;
	}
};

// for side docks, wrap SplitView's content size; for bottom, pass through
// dock containers constrain the overall size via resize; internally the split tree fills available space
// when minimized, we collapse only the content area (contentSize=0) so the tab bar stays visible
const wrapperStyle = computed(() => {
	if (minimized.value) {
		// no explicit size — wrapper shrink-wraps to just the tab bar
		return undefined;
	}
	return isSide.value ? { width: `${resize.size.value}px` } : { height: `${resize.size.value}px` };
});

// when minimized, tell PanelContainer to set content area to 0px (tab bar stays visible)
const contentSize = computed(() => (minimized.value ? 0 : undefined));
</script>

<template>
	<div
		v-show="regionPanels.length > 0 || isDropTarget"
		class="flex shrink-0"
		:class="region === 'left' ? 'flex-row-reverse' : region === 'right' ? 'flex-row' : 'flex-col'"
	>
		<!-- resize handle -->
		<div
			class="resize-handle shrink-0"
			:class="isSide ? 'resize-handle-horizontal' : 'resize-handle-vertical'"
			@pointerdown="resize.onPointerDown"
		/>

		<!-- dock content — sized by the resize handle -->
		<div class="shrink-0 overflow-hidden" :style="wrapperStyle">
			<SplitView
				:node="regionRoot.root"
				:panels="panels"
				:region="region"
				:content-size="contentSize"
				:is-drop-target="isDropTarget"
				:allowed-split-directions="['vertical']"
				@tab-mouse-up="onTabMouseUp"
			/>
		</div>
	</div>
</template>

<style scoped>
.resize-handle {
	position: relative;
	z-index: 20;
}

.resize-handle-horizontal {
	width: 1px;
	cursor: col-resize;
}

.resize-handle-horizontal::after {
	content: '';
	position: absolute;
	inset: 0 -2px;
}

.resize-handle-vertical {
	height: 1px;
	cursor: row-resize;
}

.resize-handle-vertical::after {
	content: '';
	position: absolute;
	inset: -2px 0;
}
</style>
